import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { paymentApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { Check, X } from "lucide-react";

export default function PurchaseComplete() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("pending");
  const { refresh } = useAuth();
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let interval;

    async function poll() {
      try {
        const res = await paymentApi.getCheckoutStatus(orderId);
        if (cancelled) return;
        setOrder(res.order);
        const s = res.order?.status || "pending";
        setStatus(s);
        if (s !== "pending") {
          clearInterval(interval);
          if (s === "paid") refresh();
        }
      } catch (e) {
        // Polling error — will retry on next interval.
      }
      tries.current += 1;
      if (tries.current > 60) clearInterval(interval); // give up after ~2 min
    }

    poll();
    interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [orderId, refresh]);

  if (!orderId) {
    return (
      <div className="pa-card p-6 text-center">
        <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>Missing order details.</p>
        <Link to="/library" className="pa-btn-outline mt-4 inline-block">Back to Library</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto text-center space-y-4" data-testid={IDS.purchaseCompletePage}>
      {status === "pending" && <Loading label="Confirming payment..." />}
      {status === "paid" && (
        <>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ background: "rgba(76,175,80,0.15)", border: "1px solid var(--pa-success)" }}>
            <Check color="var(--pa-success)" size={28} />
          </div>
          <h1 className="pa-serif text-3xl" style={{ color: "var(--pa-text-primary)" }}>Thank you!</h1>
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>
            Your purchase is complete. Access has been unlocked.
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/library" className="pa-btn-gold">Open Library</Link>
            <Link to="/dashboard" className="pa-btn-outline">Go to Dashboard</Link>
          </div>
        </>
      )}
      {(status === "canceled" || status === "failed") && (
        <>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ background: "rgba(244,67,54,0.15)", border: "1px solid var(--pa-error)" }}>
            <X color="var(--pa-error)" size={28} />
          </div>
          <h1 className="pa-serif text-3xl" style={{ color: "var(--pa-text-primary)" }}>Payment {status}</h1>
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>
            No charge was made. You can try again anytime.
          </p>
          <Link to="/library" className="pa-btn-outline inline-block">Back to Library</Link>
        </>
      )}
    </div>
  );
}
